import ActivityPage from '@components/Activity/ActivityPage'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { Button } from '@components/Button'
import { HeroIcon } from '@components/HeroIcon'
import { LottieEffect } from '@components/LottieEffect'
import { MediaRender } from '@components/MediaRender'
import { CONTENT_REPORT } from '@constants/features'
import { ILesson } from '@dtos/Course'
import { useActiveCourseContentStore } from '@stores/useActiveCourseContentStore'
import { useLessonTabStore } from '@stores/useLessonTabStore'
import { useLocalSearchParams } from 'expo-router'
import { useCallback, useEffect, useState } from 'react'
import { Pressable, useWindowDimensions } from 'react-native'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, Spinner, Text, View, XStack, YStack } from 'tamagui'
import { NotSupported } from '../extra_contents/NotSupported'
import { ContentsCollapsibleWeb } from './ContentsCollapsibleWeb'
import { CourseContentNavigation } from './CourseContentNavigation'
import { useDownloadContentAction } from '@hooks/useDownloadContentAction'
import { MediaRenderWrapper } from './MediaRenderWrapper'
import { NavigationControl } from '@components/NavigationControl'
import { ContentReportButton } from '@components/ContentReport/ContentReportButton'
import { useEnrollment } from '@hooks/useEnrollment'
import { useLessonNavigation } from '@hooks/useLessonNavigation'
import { useLessonProgress } from '@hooks/useLessonProgress'
import { useMediaSize } from '@hooks/useMediaSize'
import { useHasFeature } from '@hooks/useThemeParams'
import { usePremioReclameAquiPrompt } from '@hooks/usePremioReclameAquiPrompt'
import { useForumTopicsCount } from '@services/api/graphql/comments'
import { mediaReportsPlaybackProgress } from '@utils/mediaPlaybackProgress'
import { PremioReclameAquiVoteModal } from '@components/PremioReclameAqui/VoteModal'
import { useUpNavigation } from '@hooks/useUpNavigation'
import { IScreenProps } from './MobileLayout'
import { TabsContentContainer } from './TabsContentContainer'

/**
 * Consumo da aula no web, em qualquer largura. No app só entra em tela acima de
 * 1200px; abaixo disso o nativo usa o MobileLayout.
 *
 * É uma árvore só de propósito. Até 1200px ela assume o arranjo compacto (o
 * mesmo do MobileLayout), acima dele o de desktop, e o player fica sempre no
 * mesmo ponto. Quando cada largura tinha o próprio componente, redimensionar a
 * janela através de 1200px desmontava um e montava o outro, e o vídeo voltava
 * para o início. Diferença entre os arranjos entra como estilo ou como elemento
 * ao lado do player, nunca como outro pai para ele.
 */
export function LessonLayout({
  hasPreviousContent,
  hasNextContent,
  isCollapsibleOpen,
  toggleCollapsible,
  resetNextAndPreviousContent,
  onLessonIdChange,
  onLessonCompleted,
  initialLesson,
  isLoadingCourseContent = false,
}: IScreenProps) {
  const { t } = useTranslation('CourseDetails')
  const { currentTab, setCurrentTab } = useLessonTabStore()
  const { enrollment } = useEnrollment()
  const { courseId, enrollmentId, courseContentsId } = useLocalSearchParams()
  const [currentLesson, setCurrentLesson] = useState<ILesson | null>(null)
  const mediaSize = useMediaSize()
  const isCompact = mediaSize === 'xs' || mediaSize === 'sm'
  // A lista lateral é do arranjo de desktop. No compacto quem lista as aulas é
  // a barra fixa do rodapé (CourseContentsScreen), então o estado aberto da
  // lista não pode esconder setas nem mudar o cabeçalho.
  const isSideListOpen = !isCompact && Boolean(isCollapsibleOpen)
  const hasContentReport = useHasFeature(CONTENT_REPORT)

  const setActiveLessonId = useActiveCourseContentStore(
    (state) => state.setActiveLessonId,
  )

  const { canDownload, downloadContent, isDownloading } =
    useDownloadContentAction(currentLesson?.media)

  useEffect(() => {
    if (initialLesson && !isLoadingCourseContent) {
      setCurrentLesson(initialLesson)
      onLessonIdChange(initialLesson.id)
      setActiveLessonId(initialLesson.id)
    }
  }, [
    initialLesson,
    isLoadingCourseContent,
    onLessonIdChange,
    setActiveLessonId,
  ])

  const { isPreview } = useAdminPreview()

  // Convite para votar no Prêmio RA 2026: entra quando o aluno conclui uma
  // aula, uma única vez por aluno e só durante a janela da votação. Em aula de
  // vídeo/áudio espera a reprodução acabar — no app a aula é dada como
  // concluída aos 75% e o convite entraria com o vídeo ainda tocando.
  const {
    isPromptOpen: isPremioRaPromptOpen,
    handleLessonCompleted: notifyPremioRaPrompt,
    handleMediaProgress: notifyPremioRaMediaProgress,
    handleClosePrompt: closePremioRaPrompt,
  } = usePremioReclameAquiPrompt({ lessonId: currentLesson?.id })

  const handleLessonCompleted = useCallback(() => {
    onLessonCompleted?.()
    notifyPremioRaPrompt({
      waitForMediaEnd: mediaReportsPlaybackProgress(currentLesson?.media),
    })
  }, [onLessonCompleted, notifyPremioRaPrompt, currentLesson?.media])

  const {
    isLessonCompleted,
    startMediaFrom,
    finishLessonButtonStyle,
    showLessonComplete,
    setShowLessonComplete,
    handleFinishLesson,
    handleUpdateProgress,
    updateEnrollmentProgress,
    currentLessonProgress,
  } = useLessonProgress({
    currentLesson,
    // Cada arranjo mantém a régua que já tinha quando era um componente
    // próprio: 100% no desktop e o padrão do hook (75%) no compacto.
    videoCompletionThreshold: isCompact ? undefined : 100,
    onLessonCompleted: handleLessonCompleted,
  })

  const onLessonChange = useCallback(
    (lesson: ILesson) => {
      setCurrentLesson(lesson)
      onLessonIdChange(lesson.id)
      setActiveLessonId(lesson.id)
    },
    [onLessonIdChange, setActiveLessonId],
  )

  // Activity conclui a aula via mutation própria (exam answers), já resolvida
  // quando este callback roda — seguro reavaliar a navegação no servidor.
  const handleActivityProgressUpdate = useCallback(() => {
    updateEnrollmentProgress()
    handleLessonCompleted()
  }, [updateEnrollmentProgress, handleLessonCompleted])

  const {
    handlePreviousLessonPress,
    handleNextLessonPress,
    isLoadingNextOrPreviousLesson,
  } = useLessonNavigation({
    currentLesson,
    enrollmentId: enrollment?.id,
    hasPreviousContent,
    hasNextContent,
    resetNextAndPreviousContent,
    onLessonChange,
  })

  // Gate por presença de activity (e não por examQuestionsCount), pois o count
  // da lesson vem flaky do core (às vezes 0 mesmo com questões). O ActivityPage
  // busca as questões reais via GET_ACTIVITY e trata o estado vazio.
  const isActivity = !!currentLesson?.activity?.id

  const { height: windowHeight } = useWindowDimensions()

  // Desktop: o player ocupa o máximo do espaço vertical disponível
  // (proporcional ao viewport), com piso/teto. Largura preenche 100%. Vídeo,
  // PDF, Imagem e Webinar preenchem essa caixa. No compacto quem dá a altura é
  // a própria mídia (o vídeo, por exemplo, por aspect-ratio 16:9).
  const videoPlayerHeight = Math.min(
    Math.max(Math.round(windowHeight * 0.78), 500),
    800,
  )
  const mediaType = currentLesson?.media?.type
  const fillsPlayer =
    !isCompact &&
    (mediaType === 'Video' ||
      mediaType === 'Image' ||
      mediaType === 'Audio' ||
      mediaType === 'WebinarHerospark' ||
      mediaType === 'Document')

  // Compacto: o player de vídeo se dimensiona por aspect-ratio 16:9, então a
  // altura do card acompanha a largura da tela. O teto fixo de 750 foi feito
  // para celular; em tablet ele parava antes do fim do player e o conteúdo
  // seguinte subia por cima (o container tem `overflow: visible`).
  const isVideo = mediaType === 'Video'

  // Compacto: aula sem descrição, conteúdo complementar nem comentários ganha
  // um respiro no fim da página.
  const hasDescription = Boolean(currentLesson?.description)
  const hasExtraContent = Boolean((currentLesson?.extraMedias?.length ?? 0) > 0)
  const { hasTopics: hasComments } = useForumTopicsCount({
    courseContentId: currentLesson?.courseContentId
      ? String(currentLesson.courseContentId)
      : null,
    skip: !isCompact,
  })
  const compactTabsPaddingBottom =
    !hasDescription && !hasExtraContent && !hasComments
      ? windowHeight * 0.125
      : 0

  const handleBack = useUpNavigation()
  return (
    <YStack flex={isCompact ? undefined : 1} w="100%">
      <PremioReclameAquiVoteModal
        open={isPremioRaPromptOpen}
        onClose={closePremioRaPrompt}
      />
      {/* A lista lateral fica ao lado da coluna inteira (player + descrição),
          e não só da linha do player: no web ela é `sticky` e só gruda dentro
          do próprio pai. Os dois contêineres existem em qualquer largura para
          o player não trocar de árvore ao cruzar 1200px. */}
      <XStack
        gap={isSideListOpen ? '$spacing-inset-sm' : 0}
        h={isCompact ? '100%' : undefined}
        $gtSm={{
          mt: '$spacing-inset-lg',
          marginHorizontal: 48,
        }}
      >
        <YStack flex={1} h={isCompact ? '100%' : undefined}>
          <XStack h={isCompact ? '100%' : undefined}>
            <AnimatePresence presenceAffectsLayout>
              <YStack
                key="lesson-container"
                flex={1}
                maxHeight={!isActivity && !isCompact ? 1200 : 'unset'}
                w="100%"
                animation="100ms"
                animateOnly={['width']}
              >
                <YStack
                  backgroundColor="$bg-surface"
                  p="$spacing-inset-xs"
                  paddingBottom={isCompact ? '$spacing-inset-xs' : undefined}
                  gap="$spacing-inset-xs"
                  maxHeight={
                    isCompact && !isActivity && !isVideo ? 750 : 'unset'
                  }
                  $gtSm={{
                    p: '$spacing-inset-sm',
                    borderTopLeftRadius: '$border-radius-md',
                    borderTopRightRadius: '$border-radius-md',
                  }}
                >
                  {/* Header */}
                  <XStack
                    gap="$spacing-inset-xs"
                    w="100%"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {isCompact ? (
                      mediaSize === 'sm' && (
                        <XStack w="100%">
                          <CourseContentNavigation
                            visibilityType="top"
                            onPreviousPress={handlePreviousLessonPress}
                            onNextPress={handleNextLessonPress}
                            hasPreviousContent={hasPreviousContent}
                            hasNextContent={hasNextContent}
                            isLoading={isLoadingNextOrPreviousLesson}
                          />
                        </XStack>
                      )
                    ) : (
                      <>
                        <Button
                          variant="primary"
                          aria-label="Back"
                          link
                          paddingLeft={0}
                          onPress={handleBack}
                        >
                          <XStack alignItems="center">
                            <HeroIcon
                              icon={['fal', 'chevron-left']}
                              color="$accent"
                              size={16}
                            />
                            <Text
                              fontSize="$font-size-xxs"
                              ff="$mulishRegular"
                              color="$accent"
                              position="relative"
                              textDecorationLine="underline"
                            >
                              {t('Voltar')}
                            </Text>
                          </XStack>
                        </Button>

                        <XStack
                          minWidth={280}
                          justifyContent="flex-end"
                          animation="quick"
                          enterStyle={{
                            opacity: 0,
                            x: 20,
                          }}
                          exitStyle={{
                            opacity: 0,
                            x: 20,
                          }}
                          opacity={1}
                          x={0}
                        >
                          {isSideListOpen ? (
                            <View w="100%">
                              <CourseContentNavigation
                                visibilityType="top"
                                onPreviousPress={handlePreviousLessonPress}
                                onNextPress={handleNextLessonPress}
                                hasPreviousContent={hasPreviousContent}
                                hasNextContent={hasNextContent}
                                isLoading={isLoadingNextOrPreviousLesson}
                              />
                            </View>
                          ) : (
                            <Button
                              variant="primary"
                              testID="header-back-button"
                              aria-label="Expand"
                              link
                              paddingLeft={0}
                              onPress={toggleCollapsible}
                            >
                              <XStack alignItems="center">
                                <HeroIcon
                                  icon={['fal', 'chevron-left']}
                                  color="$accent"
                                  size={16}
                                />
                                <Text
                                  fontSize="$font-size-xxs"
                                  ff="$mulishRegular"
                                  color="$accent"
                                  position="relative"
                                  textDecorationLine="underline"
                                >
                                  {t('Expandir Lista')}
                                </Text>
                              </XStack>
                            </Button>
                          )}
                        </XStack>
                      </>
                    )}
                  </XStack>

                  {/* Player Controls — as setas laterais continuam montadas no
                  compacto (só escondidas) para o player não mudar de posição
                  entre os irmãos. */}
                  <XStack
                    alignItems="center"
                    justifyContent="space-between"
                    w="100%"
                    gap={isCompact ? 0 : '$spacing-inset-xxs'}
                  >
                    <View
                      display={isCompact ? 'none' : 'flex'}
                      animation="quick"
                      opacity={isSideListOpen ? 0.3 : 1}
                      pointerEvents={isSideListOpen ? 'none' : 'auto'}
                      enterStyle={{
                        opacity: 0,
                        scale: 0.95,
                      }}
                      exitStyle={{
                        opacity: 0,
                        scale: 0.95,
                      }}
                    >
                      {!isCompact && !isSideListOpen && (
                        <NavigationControl
                          testID="navigation-left-button"
                          action={handlePreviousLessonPress}
                          chevronDirection="left"
                          size={24}
                          disabled={
                            !hasPreviousContent ||
                            isSideListOpen ||
                            isLoadingNextOrPreviousLesson
                          }
                          chevronColor="$icon-primary"
                          width={52}
                          height={52}
                        />
                      )}
                    </View>

                    <XStack
                      alignItems="center"
                      justifyContent="center"
                      flex={1}
                      overflow={isActivity ? 'visible' : 'hidden'}
                      animation="quick"
                      enterStyle={{
                        opacity: 0,
                        scale: 0.95,
                      }}
                      exitStyle={{
                        opacity: 0,
                        scale: 0.95,
                      }}
                    >
                      {isActivity ? (
                        <ActivityPage
                          activityId={
                            currentLesson?.activity?.id?.toString() ?? ''
                          }
                          courseId={courseId?.toString() ?? ''}
                          enrollmentId={enrollmentId?.toString() ?? ''}
                          onPreviousPress={handlePreviousLessonPress}
                          onNextPress={handleNextLessonPress}
                          hasPreviousContent={hasPreviousContent}
                          hasNextContent={hasNextContent}
                          isLoadingNextOrPreviousLesson={
                            isLoadingNextOrPreviousLesson
                          }
                          progressCardId={
                            enrollment?.progressCard?.id?.toString() ?? ''
                          }
                          updateEnrollmentProgress={
                            handleActivityProgressUpdate
                          }
                        />
                      ) : currentLesson?.media ? (
                        <MediaRenderWrapper
                          media={currentLesson.media}
                          heightOverride={
                            fillsPlayer ? videoPlayerHeight : undefined
                          }
                        >
                          <MediaRender
                            media={currentLesson.media}
                            isImageVariant={
                              isCompact && currentLesson.media.type === 'Image'
                            }
                            thumbnailUrl={
                              isCompact
                                ? currentLesson.coverImage?.cdnFileUrl
                                : undefined
                            }
                            startMediaFrom={startMediaFrom}
                            updateLessonProgress={({
                              progress,
                              currentVideoTime,
                            }) => {
                              handleUpdateProgress(progress, currentVideoTime)
                              notifyPremioRaMediaProgress(progress)
                            }}
                            progress={currentLessonProgress?.progress}
                            fileUrl={currentLesson.media.fileUrl ?? ''}
                            isLoading={isLoadingCourseContent}
                            playerFillHeight={
                              fillsPlayer ? videoPlayerHeight : undefined
                            }
                          >
                            {/* Fallback para conteúdo não renderizável (ex.: .docx):
                            o card só baixa se receber a fileUrl da mídia. */}
                            <NotSupported
                              media={currentLesson.media}
                              fileUrl={currentLesson.media.fileUrl ?? undefined}
                            />
                          </MediaRender>
                        </MediaRenderWrapper>
                      ) : null}
                    </XStack>

                    <View
                      display={isCompact ? 'none' : 'flex'}
                      animation="quick"
                      opacity={isSideListOpen ? 0.3 : 1}
                      pointerEvents={isSideListOpen ? 'none' : 'auto'}
                      enterStyle={{
                        opacity: 0,
                        scale: 0.95,
                      }}
                      exitStyle={{
                        opacity: 0,
                        scale: 0.95,
                      }}
                    >
                      {!isCompact && !isSideListOpen && (
                        <NavigationControl
                          testID="navigation-right-button"
                          action={handleNextLessonPress}
                          chevronDirection="right"
                          size={24}
                          disabled={
                            !hasNextContent ||
                            isSideListOpen ||
                            isLoadingNextOrPreviousLesson
                          }
                          chevronColor="$icon-primary"
                          width={52}
                          height={52}
                        />
                      )}
                    </View>
                  </XStack>

                  {/* Content General Information */}
                  {!isActivity && (
                    <YStack
                      gap="$spacing-inset-sm"
                      paddingHorizontal={
                        isCompact
                          ? 0
                          : !isSideListOpen
                            ? 72
                            : '$spacing-inset-xxs'
                      }
                      animation="quick"
                      enterStyle={{
                        opacity: 0,
                        y: 20,
                      }}
                      exitStyle={{
                        opacity: 0,
                        y: 20,
                      }}
                    >
                      <YStack gap="$spacing-inset-sm">
                        <Text
                          color="$text-primary"
                          fontSize="$font-size-lg"
                          fontFamily="$mulishBold"
                          fontWeight="700"
                          numberOfLines={2}
                        >
                          {currentLesson?.title || ''}
                        </Text>

                        <XStack alignItems="center">
                          <XStack
                            alignItems="center"
                            justifyContent="space-between"
                            gap="$spacing-inset-sm"
                            flex={1}
                          >
                            <XStack alignItems="center" gap="$spacing-inset-sm">
                              <View
                                position="relative"
                                // Visão do produtor não conclui aula: o servidor
                                // recusa a escrita, então o botão não aparece.
                                display={isPreview ? 'none' : 'flex'}
                              >
                                <Pressable
                                  testID="finish-lesson-button"
                                  onPress={handleFinishLesson}
                                  disabled={isLessonCompleted}
                                >
                                  <YStack
                                    gap="$spacing-inset-quarck"
                                    alignItems="center"
                                    p="$spacing-inset-nano"
                                  >
                                    <HeroIcon
                                      icon={finishLessonButtonStyle.icon}
                                      size={21}
                                      color={finishLessonButtonStyle.iconColor}
                                    />
                                    <Text
                                      color="$text-primary"
                                      fontSize="$font-size-sm"
                                      fontFamily="$mulishRegular"
                                      lineHeight={24}
                                    >
                                      {finishLessonButtonStyle.label}
                                    </Text>
                                  </YStack>
                                </Pressable>
                                <View
                                  testID="lesson-complete-overlay"
                                  position="absolute"
                                  top={0}
                                  left={0}
                                  right={0}
                                  bottom={30}
                                  // Sem isto a View cobre o ícone do botão de
                                  // concluir e engole o clique — só o label, fora
                                  // da faixa absoluta, continuava clicável.
                                  pointerEvents="none"
                                >
                                  {showLessonComplete && (
                                    <LottieEffect
                                      source={require('@assets/animations/lesson_completed.json')}
                                      onComplete={() =>
                                        setShowLessonComplete(false)
                                      }
                                    />
                                  )}
                                </View>
                              </View>

                              {/* Mesma marcação do botão de concluir (ícone em
                              cima, label embaixo) para os dois saírem
                              idênticos. */}
                              {canDownload && (
                                <Pressable
                                  testID="download-content-button"
                                  onPress={downloadContent}
                                  disabled={isDownloading}
                                >
                                  <YStack
                                    gap="$spacing-inset-quarck"
                                    alignItems="center"
                                    p="$spacing-inset-nano"
                                  >
                                    {isDownloading ? (
                                      <Spinner size="small" color="$accent" />
                                    ) : (
                                      <HeroIcon
                                        icon={['fal', 'download']}
                                        size={21}
                                        color="$accent"
                                      />
                                    )}
                                    <Text
                                      color="$text-primary"
                                      fontSize="$font-size-sm"
                                      fontFamily="$mulishRegular"
                                      lineHeight={24}
                                    >
                                      {isDownloading
                                        ? t('download.loading')
                                        : t('download.label')}
                                    </Text>
                                  </YStack>
                                </Pressable>
                              )}
                            </XStack>
                            {/* O compacto sempre respeitou a flag de denúncia; o
                            desktop sempre mostrou o botão. */}
                            {(hasContentReport || !isCompact) && (
                              <View
                                marginRight={
                                  isCompact ? '$spacing-inset-xs' : 0
                                }
                              >
                                <ContentReportButton
                                  schoolProductId={String(
                                    enrollment?.schoolProduct?.id,
                                  )}
                                  courseId={String(courseId)}
                                  lessonId={String(currentLesson?.id)}
                                  courseContentId={String(courseContentsId)}
                                />
                              </View>
                            )}
                          </XStack>
                          {mediaSize === 'xs' && (
                            <View bottom={2}>
                              <CourseContentNavigation
                                onPreviousPress={handlePreviousLessonPress}
                                onNextPress={handleNextLessonPress}
                                hasPreviousContent={hasPreviousContent}
                                hasNextContent={hasNextContent}
                                isLoading={isLoadingNextOrPreviousLesson}
                              />
                            </View>
                          )}
                        </XStack>
                      </YStack>
                    </YStack>
                  )}
                </YStack>
                {/* Compacto: abas e conteúdo delas logo abaixo do card. Desktop: só
                as abas aqui; o conteúdo vai para fora da linha, abaixo. */}
                {isCompact ? (
                  <YStack paddingBottom={compactTabsPaddingBottom}>
                    <TabsContentContainer
                      currentLesson={currentLesson}
                      currentTab={currentTab}
                      onTabChange={setCurrentTab}
                      description={currentLesson?.description ?? ''}
                      extraMedias={currentLesson?.extraMedias ?? []}
                    />
                  </YStack>
                ) : (
                  !isActivity && (
                    <TabsContentContainer
                      currentLesson={currentLesson}
                      onTabChange={setCurrentTab}
                      withoutTabContent={true}
                      description={currentLesson?.description ?? ''}
                      extraMedias={currentLesson?.extraMedias ?? []}
                    />
                  )
                )}
              </YStack>
            </AnimatePresence>
          </XStack>
          {!isCompact && !isActivity && (
            <YStack flex={1} h="100%" paddingBottom={200}>
              <TabsContentContainer
                currentLesson={currentLesson}
                currentTab={currentTab}
                withoutTabContent={false}
                withoutTabs={true}
                description={currentLesson?.description ?? ''}
                extraMedias={currentLesson?.extraMedias ?? []}
              />
            </YStack>
          )}
        </YStack>

        {isSideListOpen && <ContentsCollapsibleWeb type="topOpened" />}
      </XStack>
    </YStack>
  )
}
